import { useContext, useState } from "react";
import { useModal } from "react-modal-hook";
import { fontWeights } from "common/utils/styles";
import {
  GetWorkflowQuery,
  useDeleteWorkflowMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn } from "../Common/__styles__/Layout";
import { CommonModal } from "../Common/CommonModal";
import Modal from "../Common/Modal";
import { Body } from "../Common/Typography";
import Checkbox from "../Inputs/Checkbox";

type DeleteWorkflowModalProps = {
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
  onCancel: () => void;
  onSuccess: () => void;
};

export const DeleteWorkflowModal = ({
  workflow,
  onSuccess,
  onCancel,
}: DeleteWorkflowModalProps) => {
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const { user, admin } = useContext(AuthContext);

  const [dataDeletedConfirmed, setDataDeletedConfirmed] = useState(false);
  const [tasksDeletedConfirmed, setTasksDeletedConfirmed] = useState(false);
  const [collaboratorsRemovedConfirmed, setCollaboratorsRemovedConfirmed] =
    useState(false);

  const allConfirmed =
    dataDeletedConfirmed &&
    tasksDeletedConfirmed &&
    collaboratorsRemovedConfirmed;

  const [deleteWorkflowMutation, { loading }] = useDeleteWorkflowMutation({
    onCompleted: () => {
      addSuccessToast("Your workflow was successfully deleted");
      onSuccess();
      track("Workflow deleted", {
        workflowId: workflow.id,
        workflowDefinitionId: workflow.workflowDefinition.id,
        workflowDefinitionName: workflow.workflowDefinition.name,
        userId: user?.id ?? admin?.id,
        userEmail: user?.email ?? admin?.email,
      });
    },
    onError: () => {
      addErrorToast(
        "There was an error deleting this workflow. Please try again. If the problem persists, please email support@withforerunner.com",
      );
    },
  });

  const onSubmit = async () => {
    await deleteWorkflowMutation({
      variables: {
        workflowId: workflow.id,
      },
    });
  };

  const content = (
    <FlexColumn gap="m">
      <Body size="default" type="regular">
        By deleting this workflow, you confirm that:
      </Body>
      <FlexColumn gap="s">
        <Checkbox
          label="All records and uploaded files associated with this workflow will be deleted. *"
          value={dataDeletedConfirmed}
          onChange={setDataDeletedConfirmed}
        />
        <Checkbox
          label="Any tasks associated with this workflow will be deleted. *"
          value={tasksDeletedConfirmed}
          onChange={setTasksDeletedConfirmed}
        />
        <Checkbox
          label="This workflow will no longer be accessible to the people you've shared it with. *"
          value={collaboratorsRemovedConfirmed}
          onChange={setCollaboratorsRemovedConfirmed}
        />
      </FlexColumn>
      <Body size="default" type="regular">
        This action <span style={{ fontWeight: fontWeights.bold }}>cannot</span>{" "}
        be undone.
      </Body>
    </FlexColumn>
  );

  return (
    <CommonModal
      title="Delete workflow"
      subtitle={workflow.workflowDefinition.name}
      content={content}
      onSubmit={onSubmit}
      onCancel={onCancel}
      loading={loading}
      disabled={!allConfirmed || loading}
      primaryButtonText="Delete"
      variant="destructive"
      size="medium"
      dataTestId="delete-workflow-modal"
    />
  );
};

export const useDeleteWorkflowModal = ({
  workflow,
  onSuccess,
}: {
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
  onSuccess: () => void;
}) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <DeleteWorkflowModal
          workflow={workflow}
          onCancel={hideModal}
          onSuccess={() => {
            hideModal();
            onSuccess();
          }}
        />
      </Modal>
    ),
    [workflow, onSuccess],
  );

  return [showModal, hideModal] as const;
};
